import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Select, MultiSelect, TextInput } from '@mantine/core';
import type { ReportSessionDate, ReportSessionOption } from 'shared-models/browser';
import { Button } from '../../../../components/Button';
import navyModal from '../../../../components/Modal/navy-modal.module.scss';
import { reportsService } from '../../../../services/reports/reports.service';
import { useAccountantReportController } from './useAccountantReportController';
import { AccountantReportTable } from './AccountantReportTable';
import { formatReportDate } from './accountant-report.utils';
import classes from './accountant-report-modal.module.scss';

export type AccountantReportModalProps = {
  opened: boolean;
  onClose: () => void;
  tenantId: string;
  employeeSession: string;
};

// Summary: accountant-report modal — composition only; state/async in the controller hook.
const AccountantReportModalComponent: React.FC<AccountantReportModalProps> = ({ opened, onClose, tenantId, employeeSession }) => {
  const ctrl = useAccountantReportController({ opened, tenantId, employeeSession });
  const [dates, setDates] = useState<ReportSessionDate[]>([]);
  const [pickedDate, setPickedDate] = useState<string | null>(null);
  const [daySessions, setDaySessions] = useState<ReportSessionOption[]>([]);

  // Load selectable closed-session days when switching into the specific-sessions mode.
  useEffect(() => {
    if (!opened || ctrl.mode !== 'sessions' || !employeeSession) {
      return;
    }
    void reportsService.listSessionDates(tenantId, employeeSession).then(setDates).catch(() => undefined);
  }, [opened, ctrl.mode, tenantId, employeeSession]);

  const handlePickDate = useCallback(
    (value: string | null) => {
      setPickedDate(value);
      ctrl.setSessionIds([]);
      if (!value) {
        setDaySessions([]);
        return;
      }
      void reportsService.getSessionsForDate(tenantId, value, employeeSession).then(setDaySessions).catch(() => undefined);
    },
    [tenantId, employeeSession, ctrl],
  );

  const hasReport = ctrl.view !== null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<strong>דו"ח רואה חשבון</strong>}
      size="90%"
      centered
      radius="md"
      dir="rtl"
      closeButtonProps={{ 'data-testid': 'accountant-report-close' } as Record<string, string>}
      classNames={{ root: navyModal.root, content: classes.modalContent, body: classes.modalBody }}
    >
      <div className={classes.controls}>
        <div className={classes.toggle}>
          <button
            type="button"
            data-testid="accountant-report-mode-range"
            className={`${classes.toggleBtn} ${ctrl.mode === 'range' ? classes.toggleActive : ''}`}
            onClick={() => ctrl.setMode('range')}
          >
            טווח תאריכים
          </button>
          <button
            type="button"
            data-testid="accountant-report-mode-sessions"
            className={`${classes.toggleBtn} ${ctrl.mode === 'sessions' ? classes.toggleActive : ''}`}
            onClick={() => ctrl.setMode('sessions')}
          >
            משמרות נבחרות
          </button>
        </div>

        {ctrl.mode === 'range' ? (
          <div className={classes.rangeRow}>
            <TextInput
              type="date"
              data-testid="accountant-report-from"
              label="מתאריך"
              value={ctrl.range.from}
              onChange={(event) => ctrl.setRange({ ...ctrl.range, from: event.currentTarget.value })}
            />
            <TextInput
              type="date"
              data-testid="accountant-report-to"
              label="עד תאריך"
              value={ctrl.range.to}
              onChange={(event) => ctrl.setRange({ ...ctrl.range, to: event.currentTarget.value })}
            />
          </div>
        ) : (
          <div className={classes.rangeRow}>
            <Select
              data-testid="accountant-report-day"
              label="יום"
              placeholder="בחר יום"
              value={pickedDate}
              onChange={handlePickDate}
              data={dates.map((d) => ({ value: d.date, label: `${formatReportDate(d.date)} (${d.sessionCount})` }))}
              searchable
            />
            <MultiSelect
              data-testid="accountant-report-sessions"
              label="משמרות"
              placeholder="בחר משמרות"
              value={ctrl.sessionIds.map(String)}
              onChange={(values) => ctrl.setSessionIds(values.map(Number))}
              data={daySessions.map((s) => ({ value: String(s.id), label: s.name }))}
              disabled={daySessions.length === 0}
            />
          </div>
        )}

        <div className={classes.actions}>
          <Button variant="save" onClick={() => { void ctrl.generate(); }} disabled={ctrl.loading} dataTestId="accountant-report-generate">
            {ctrl.loading ? 'מפיק...' : 'הפק דוח'}
          </Button>
          <Button variant="save" onClick={() => { void ctrl.exportXlsx(); }} disabled={!hasReport || ctrl.exporting} dataTestId="accountant-report-export">
            {ctrl.exporting ? 'מייצא...' : 'ייצוא לאקסל'}
          </Button>
          <Button variant="save" onClick={ctrl.print} disabled={!hasReport} dataTestId="accountant-report-print">
            הדפסה
          </Button>
        </div>
      </div>

      {hasReport && (
        <div className={classes.printable} id="accountant-report-printable">
          <h2 className={classes.printTitle}>
            דו"ח רואה חשבון — {ctrl.view!.businessName}
          </h2>
          <AccountantReportTable view={ctrl.view!} />
        </div>
      )}
    </Modal>
  );
};

export const AccountantReportModal = React.memo(AccountantReportModalComponent);
